import { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import PageNotFound from "./Components/Layouts/PageNotFound";
import RedirectToPrevious from "./Components/Layouts/RedirectToPrevious ";
import PrivateRoute from "./Components/Layouts/ProtectRoute/PrivateRoute";
import { isAuthenticated } from "./Components/Layouts/ProtectRoute/Auth";
import { OrbitLoader } from "./Components/Common/Loader";

const AddOrganization = lazy(() =>
  import("./Components/Elements/Organization/AddOrganization")
);
const ManageOrganization = lazy(() =>
  import("./Components/Elements/Organization/ManageOrganization")
);
const ManageAttendance = lazy(() =>
  import("./Components/Elements/Attendance/ManageAttendance")
);
const LoginDashboard = lazy(() =>
  import("./Components/Screens/LoginDashboard")
);
const Dashboard = lazy(() => import("./Components/Screens/Dashboard"));
const GlobalConfig = lazy(() => import("./Components/Screens/GlobalConfig"));
const GlobalConfigTemplates = lazy(() =>
  import("./Components/Elements/GlobalConfigTemplates/GlobalConfigTemplates")
);
const ManageLeads = lazy(() =>
  import("./Components/Elements/Leads/ManageLeads")
);
const AddLeads = lazy(() => import("./Components/Elements/Leads/AddLeads"));
const UpdateLeads = lazy(() =>
  import("./Components/Elements/Leads/UpdateLeads")
);
const AppBarLayout = lazy(() => import("./Components/Layouts/AppBarLayout"));
const ManageData = lazy(() => import("./Components/Elements/Data/ManageData"));
const AddData = lazy(() => import("./Components/Elements/Data/AddData"));
const UpdateData = lazy(() => import("./Components/Elements/Data/UpdateData"));
const DataPreview = lazy(() =>
  import("./Components/Elements/Data/DataPreview")
);

// ManageProject;
const ManageProject = lazy(() => import("./Components/Screens/ManageProject"));
const ProjectPreview = lazy(() =>
  import("./Components/Elements/Projects/ProjectPreview")
);

const AddProject = lazy(() =>
  import("./Components/Elements/Projects/AddProject")
);
const UpdateProject = lazy(() =>
  import("./Components/Elements/Projects/UpdateProject")
);

// ManageProperty
const ManageProperty = lazy(() =>
  import("./Components/Screens/ManageProperty")
);

const AddProperty = lazy(() =>
  import("./Components/Elements/Properties/AddProperty")
);
const UpdateProperty = lazy(() =>
  import("./Components/Elements/Properties/UpdateProperty")
);
const PropertyPreview = lazy(() =>
  import("./Components/Elements/Properties/PropertyPreview")
);
const CommingSoon = lazy(() => import("./Components/Layouts/CommingSoon"));
const ReportsAgency = lazy(() =>
  import("./Components/Elements/Reports/ReportsAgency")
);
const ReportsActivity = lazy(() =>
  import("./Components/Elements/Reports/ReportsActivity")
);
const ManageTask = lazy(() => import("./Components/Elements/Task/ManageTask"));
const ManageUsers = lazy(() => import("./Components/Screens/ManageUsers"));
const AddUsers = lazy(() => import("./Components/Elements/Users/AddUsers"));
const UserDetails = lazy(() =>
  import("./Components/Elements/Users/UserDetails")
);
const UpdateUsers = lazy(() =>
  import("./Components/Elements/Users/UpdateUsers")
);
const ManageTeams = lazy(() =>
  import("./Components/Elements/Teams/ManageTeams")
);
const ManageRolesPermission = lazy(() =>
  import("./Components/Elements/RolesPermission/ManageRolesPermission")
);

const Routers = () => {
  return (
    <>
      <Router>
        <Suspense fallback={<OrbitLoader />}>
          <Routes>
            {/* Redirect root to /dashboard only if logged in */}
            <Route
              path="/"
              element={
                isAuthenticated() ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route path="/login" element={<LoginDashboard />} />

            {/* 🧩 All dashboard routes go under this */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <AppBarLayout />
                </PrivateRoute>
              }
            >
              <Route index element={<Dashboard />} /> {/* /dashboard */}
              {/* global-config */}
              <Route path="global-config">
                <Route
                  path="/dashboard/global-config/templates"
                  element={<Navigate to="/dashboard/global-config" />}
                />
                <Route
                  path="/dashboard/global-config"
                  element={<GlobalConfig />}
                />

                <Route
                  path="templates/lead-templates"
                  element={<GlobalConfigTemplates />}
                />
              </Route>
              {/* 🟢 Leads Section */}
              <Route path="leads">
                <Route
                  path="/dashboard/leads"
                  element={<Navigate to="/dashboard/leads/manage-lead" />}
                />
                <Route path="manage-lead" element={<ManageLeads />} />
                <Route path="manage-lead/:ids" element={<ManageLeads />} />
                <Route path="add-lead" element={<AddLeads />} />
                <Route
                  path="update-lead/:name/:leadId"
                  element={<UpdateLeads />}
                />
              </Route>
              {/* 🔵 Data Section */}
              <Route path="data">
                <Route
                  path="/dashboard/data"
                  element={<Navigate to="/dashboard/data/manage-data" />}
                />
                <Route path="manage-data" element={<ManageData />} />
                <Route path="add-data" element={<AddData />} />
                <Route
                  path="update-data/:name/:dataId"
                  element={<UpdateData />}
                />
                <Route
                  path="preview-data/:name/:dataId"
                  element={<DataPreview />}
                />
              </Route>
              {/* 🔵 Report Section */}
              <Route path="reports">
                <Route
                  path="/dashboard/reports"
                  element={<Navigate to="/dashboard/reports/activity" />}
                />
                <Route path="activity" element={<ReportsActivity />} />
                <Route path="agency" element={<ReportsAgency />} />
                {/* 🔵 Report/Data Section */}
                <Route path="data">
                  <Route
                    path="/dashboard/reports/data"
                    element={<Navigate to="/dashboard/reports/data/call" />}
                  />
                  <Route path="call" element={<CommingSoon />} />
                  <Route path="activity" element={<CommingSoon />} />
                  <Route path="status" element={<CommingSoon />} />
                  <Route path="project" element={<CommingSoon />} />
                  <Route path="source" element={<CommingSoon />} />
                  <Route path="subsource" element={<CommingSoon />} />
                </Route>
                {/* 🔵 Report/Leads Section */}
                <Route path="leads">
                  <Route
                    path="/dashboard/reports/leads"
                    element={<Navigate to="/dashboard/reports/leads/call" />}
                  />
                  <Route path="call" element={<CommingSoon />} />
                  <Route path="activity" element={<CommingSoon />} />
                  <Route path="status" element={<CommingSoon />} />
                  <Route path="project" element={<CommingSoon />} />
                  <Route path="source" element={<CommingSoon />} />
                  <Route path="subsource" element={<CommingSoon />} />
                  <Route path="substatus" element={<CommingSoon />} />
                  <Route path="user-vs-source" element={<CommingSoon />} />
                  <Route path="user-vs-subsource" element={<CommingSoon />} />
                </Route>
                <Route path="project-substatus" element={<CommingSoon />} />
                <Route path="received-date-source" element={<CommingSoon />} />
                <Route path="visit-meeting-report" element={<CommingSoon />} />
              </Route>
              {/* 🔵 Projects Section */}
              <Route path="projects">
                <Route
                  path="/dashboard/projects"
                  element={
                    <Navigate to="/dashboard/projects/manage-projects" />
                  }
                />
                <Route path="manage-projects" element={<ManageProject />} />
                <Route path="add-projects" element={<AddProject />} />
                <Route
                  path="update-projects/:projectTitle/:projectId"
                  element={<UpdateProject />}
                />
              </Route>
              {/* 🔵 Properties Section */}
              <Route path="properties">
                <Route
                  path="/dashboard/properties"
                  element={
                    <Navigate to="/dashboard/properties/manage-properties" />
                  }
                />
                <Route path="manage-properties" element={<ManageProperty />} />
                <Route path="manage-properties" element={<ManageProperty />} />
                <Route path="add-properties" element={<AddProperty />} />
                <Route
                  path="update-property/:propertyTitle/:propertyId"
                  element={<UpdateProperty />}
                />
              </Route>
              {/* 🔵 attendance Section */}
              <Route path="attendance" element={<ManageAttendance />} />
              {/* 🔵 Task Section */}
              <Route path="task">
                <Route
                  path="/dashboard/task"
                  element={<Navigate to="/dashboard/task/manage-task" />}
                />
                <Route path="manage-task" element={<ManageTask />} />
              </Route>
              {/* 🔵 team Section */}
              <Route path="team">
                <Route
                  path="/dashboard/team"
                  element={<Navigate to="/dashboard/team/manage-users" />}
                />
                <Route path="manage-users" element={<ManageUsers />} />
                <Route path="add-users" element={<AddUsers />} />
                <Route
                  path="user-details/:userName/:userId"
                  element={<UserDetails />}
                />
                <Route
                  path="update-user/:userName/:userId"
                  element={<UpdateUsers />}
                />
                <Route path="manage-teams" element={<ManageTeams />} />
                <Route path="manage-members/:ids" element={<ManageUsers />} />
                <Route path="manage-role" element={<ManageRolesPermission />} />
              </Route>
              {/* organization Section */}
              <Route
                path={
                  "/dashboard/organization" ||
                  "/dashboard/organization/profile-dashboard"
                }
                element={
                  <Navigate to="/dashboard/organization/profile-dashboard/basic-details" />
                }
              />
              <Route
                path="organization/profile-dashboard/basic-details"
                element={<ManageOrganization />}
              />
              <Route
                path="organization/profile-dashboard/add-organization"
                element={<AddOrganization />}
              />
            </Route>

            <Route path="/external">
              <Route path="property-preview" element={<RedirectToPrevious />} />
              {/* eg. /external/property-preview/avinash123/AV321852 */}
              <Route
                path="property-preview/:username/:propertyid"
                element={<PropertyPreview />}
              />
              <Route
                path="project-preview/:username/:projectId"
                element={<ProjectPreview />}
              />
            </Route>

            {/* 404 Not found */}
            <Route path="/error_404" element={<PageNotFound />} />
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </Suspense>
      </Router>
    </>
  );
};

export default Routers;
